import { forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** The server's message for this field. Rendered, and wired via aria. */
  error?: string;
  hint?: string;
}

/**
 * A labelled input with its error and hint wired up.
 *
 * The label, the error and the input are associated by id rather than by
 * nesting, so a screen reader announces the field's name AND its error - a
 * bare red border under an unlabelled box tells someone using one nothing at
 * all. `aria-invalid` is what actually flags the state; the red border is the
 * sighted equivalent.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hintId = `${inputId}-hint`;

    return (
      <div className="flex flex-col gap-1.5">
        {label ? (
          // The asterisk is a SIBLING of the label, not a child. aria-hidden
          // does not remove a node from label.textContent, so nesting it makes
          // the field's accessible name "Customer*" - announced as "Customer
          // star". `required` on the input is what actually conveys it.
          <span className="flex items-center text-sm font-medium text-foreground">
            <label htmlFor={inputId}>{label}</label>
            {props.required ? (
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            ) : null}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={cn(
            // Taller under a finger - see the note on Button's sizes. A
            // field with 14px text is also where iOS decides to zoom the page
            // on focus; 16px text at 44px tall is what stops it, and both
            // key on the pointer rather than the width so an iPad gets them.
            'min-h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-sm transition-colors touch:min-h-11 touch:text-base',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-destructive' : 'border-input',
            className,
          )}
          {...props}
        />
        {error ? (
          <p id={errorId} className="text-xs text-destructive">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; error?: string }>(
  ({ className, label, error, id, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = id ?? generatedId;
    return (
      <div className="flex flex-col gap-1.5">
        {label ? (
          <label htmlFor={textareaId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        ) : null}
        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={error ? true : undefined}
          className={cn(
            'min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm touch:text-base',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
            error ? 'border-destructive' : 'border-input',
            className,
          )}
          {...props}
        />
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
